import {
	type OrderPaymentResponseDto,
	OrderPaymentType,
} from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useDeleteOrderPaymentMutation } from "Order/Modules/Payment/Api/Mutation/useDeletePaymentMutation";
import { useUpdateOrderPaymentMutation } from "Order/Modules/Payment/Api/Mutation/useUpdatePaymentMutation";
import { ActionButton } from "Order/Modules/Payment/Components/ActionButton";
import {
	type EditPaymentFormModel,
	getEditPaymentFormModelSchema,
} from "Order/Modules/Payment/Components/Forms/Schema/editPaymentFormSchema";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Delete } from "@mui/icons-material";
import { Grid2, Stack } from "@mui/material";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

type EditPaymentFormProps = {
	payment: OrderPaymentResponseDto;
	orderId: number;
};

const paymentResource = Resources.base.payment.form;
const selectOptionsResource = Resources.base.payment.selectOptions;

export function EditPaymentForm({ payment, orderId }: EditPaymentFormProps) {
	const { t } = useResource();
	const form = useForm<EditPaymentFormModel>({
		resolver: zodResolver(getEditPaymentFormModelSchema()),
		defaultValues: {
			dateCreated: payment.dateCreated,
			type: payment.type,
			amount: payment.amount,
		},
	});

	const {
		control,
		formState: { errors, isDirty },
	} = form;

	const { mutate: updatePayment, isPending: isPendingUpdate } =
		useUpdateOrderPaymentMutation(orderId, payment.id);

	const { mutate: deletePayment, isPending: isPendingDelete } =
		useDeleteOrderPaymentMutation(orderId, payment.id);

	useEffect(() => {
		form.reset({
			type: payment.type,
			amount: payment.amount,
			dateCreated: payment.dateCreated,
		});
	}, [payment]);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError(updatePayment);
	};

	const hasPaymentUpdatePermission = hasPermission(
		UserPermissions.OrderPaymentModule.update,
	);

	const hasPaymentDeletePermission = hasPermission(
		UserPermissions.OrderPaymentModule.delete,
	);

	return (
		<Stack width="100%" gap={1}>
			<Grid2 container mb={1} spacing={{ xs: 0, sm: 2 }}>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<DatePicker
						name="dateCreated"
						label={t(paymentResource.dateCreated)}
						control={control}
						errors={errors}
						disabled={!hasPaymentUpdatePermission}
						readOnly
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<Select
						id="type"
						name="type"
						label={t(paymentResource.type)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasPaymentUpdatePermission}
						fullWidth
						options={
							// 480 - Remove "Order deposit" payment type
							createSelectOptions(
								OrderPaymentType,
								t,
								selectOptionsResource.paymentType,
							).filter((i) => i.value !== OrderPaymentType.orderDepositPayment)
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 3 }}>
					<Input
						id="amount"
						name="amount"
						label={t(paymentResource.amount)}
						variant="outlined"
						control={control}
						errors={errors}
						disabled={!hasPaymentUpdatePermission}
						fullWidth
						isFloat
						isNumber
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 1 }}>
					{!isDirty && (
						<ActionButton
							color="warning"
							disabled={!hasPaymentDeletePermission}
							loading={isPendingDelete}
							onClick={deletePayment}
						>
							<Delete />
						</ActionButton>
					)}
					{isDirty && (
						<ActionButton
							color="success"
							disabled={!hasPaymentUpdatePermission}
							loading={isPendingUpdate}
							onClick={handleSubmitUpdate}
						>
							<Check />
						</ActionButton>
					)}
				</Grid2>
			</Grid2>
		</Stack>
	);
}
